import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { serverStore } from '../../stores/server.store';
import { Card, CardHeader, CardTitle, CardContent } from '../design/Card';
import { Button } from '../design/Button';
import { Select } from '../design/Select';
import { apiFetch } from '../../utils/api';
import toast from 'react-hot-toast';
import { Zap, Check, Download, Loader2, Cpu, Info, RotateCcw, Gauge } from 'lucide-react';
import { FunLoader } from '../common/FunLoader';

interface PerfMod { slug: string; name: string; category: 'safe' | 'optional'; desc: string; status: 'present' | 'available' | 'incompatible'; version?: string; }
interface JavaInfo { major: number; path: string; name: string; }
interface OptimizeInfo { loader: string; mcVersion: string | null; mods: PerfMod[]; jvm: { profile: string; profiles: string[]; javaPath: string; javas: JavaInfo[] }; }

const PROFILE_INFO: Record<string, { label: string; desc: string }> = {
    stabil: { label: 'Stabil (Aikar/G1)', desc: 'Bewährter Standard: RAM fest vorbelegt, maximale Stabilität. Java 17+.' },
    zgc: { label: 'ZGC (niedrige Latenz)', desc: 'Moderner GC mit ~0,2ms-Pausen statt ~35ms — spürbar smoother bei vielen Spielern. RAM fest vorbelegt. Java 21+.' },
    elastisch: { label: 'Elastisch (RAM-Rückgabe)', desc: 'ZGC + gibt ungenutzten RAM nach ~5 Min ans System zurück — ideal um mehrere Server auf einer Box zu fahren. Java 21+.' },
};

export const OptimizeTab = observer(() => {
    const server = serverStore.activeServer;
    const [info, setInfo] = useState<OptimizeInfo | null>(null);
    const [loading, setLoading] = useState(true);
    const [installing, setInstalling] = useState(false);
    const [applying, setApplying] = useState(false);
    const [selOptional, setSelOptional] = useState<Set<string>>(new Set());
    const [profile, setProfile] = useState('stabil');
    const [javaMajor, setJavaMajor] = useState<number>(21);

    const load = async () => {
        if (!server) return;
        setLoading(true);
        try {
            const res = await apiFetch(`/api/servers/${server.id}/optimize`);
            if (res.ok) {
                const d = await res.json();
                setInfo(d);
                setProfile(d.jvm?.profile || 'stabil');
                // aktuelle Java-Version aus dem Pfad ableiten (falls gesetzt)
                const cur = (d.jvm?.javas || []).find((j: JavaInfo) => d.jvm.javaPath?.includes(j.name));
                setJavaMajor(cur?.major || d.jvm?.javas?.[0]?.major || 21);
            }
        } catch { toast.error('Analyse fehlgeschlagen'); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [server?.id]);

    if (!server) return null;

    const safeAvailable = info?.mods.filter((m) => m.category === 'safe' && m.status === 'available') || [];
    const optionalMods = info?.mods.filter((m) => m.category === 'optional') || [];
    const presentCount = info?.mods.filter((m) => m.status === 'present').length || 0;

    const runOptimize = async () => {
        const slugs = [...safeAvailable.map((m) => m.slug), ...Array.from(selOptional)];
        if (slugs.length === 0) { toast('Nichts auszuwählen — alles Relevante ist schon installiert.'); return; }
        setInstalling(true);
        try {
            const res = await apiFetch(`/api/servers/${server.id}/optimize`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ slugs })
            });
            const d = await res.json();
            if (d.success) {
                toast.success(`${d.installed.length} Mod(s) installiert${d.skipped.length ? `, ${d.skipped.length} übersprungen` : ''}. Aktiv nach Neustart.`);
                setSelOptional(new Set());
                load();
            } else toast.error(d.message || 'Installation fehlgeschlagen');
        } catch { toast.error('Netzwerkfehler'); }
        finally { setInstalling(false); }
    };

    const applyProfile = async () => {
        setApplying(true);
        try {
            const res = await apiFetch(`/api/servers/${server.id}/jvm-profile`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ profile, javaMajor })
            });
            const d = await res.json();
            if (d.success) {
                toast.success(`Profil "${PROFILE_INFO[profile]?.label || profile}" gesetzt — aktiv nach Neustart.`);
                if (d.customScript) toast('Hinweis: eigenes Startskript erkannt — nur user_jvm_args.txt angepasst.', { icon: 'ℹ️', duration: 7000 });
                load();
            } else toast.error(d.message || 'Fehlgeschlagen');
        } catch { toast.error('Netzwerkfehler'); }
        finally { setApplying(false); }
    };

    if (loading) return <div className="flex items-center justify-center py-24"><FunLoader size="md" showProgress={false} /></div>;
    if (!info) return <div className="text-fg-muted text-sm p-6">Analyse nicht möglich.</div>;

    const StatusBadge = ({ s }: { s: PerfMod['status'] }) => s === 'present'
        ? <span className="text-[10px] bg-green-500/10 text-green-400 border border-green-500/20 px-2 py-0.5 rounded-md flex items-center gap-1"><Check size={10} /> Installiert</span>
        : s === 'available'
        ? <span className="text-[10px] bg-ember/10 text-ember border border-ember/20 px-2 py-0.5 rounded-md">Verfügbar</span>
        : <span className="text-[10px] bg-surface-2 text-fg-muted border border-line px-2 py-0.5 rounded-md">Nicht kompatibel</span>;

    return (
        <div className="space-y-6 animate-in fade-in duration-300">
            {/* Ehrliche Erwartung */}
            <div className="flex items-start gap-2 text-xs text-fg-muted bg-surface-2 border border-line rounded-xl px-4 py-3">
                <Info size={14} className="mt-0.5 shrink-0 text-ember" />
                <span>
                    <strong className="text-white">Was das bringt:</strong> weniger RAM (bis −40 % bei Packs ohne diese Mods), bessere TPS,
                    und mit dem elastischen Profil RAM-Rückgabe ans System (mehr Server pro Box). <strong className="text-white">Was es nicht kann:</strong>
                    {' '}die Welt-Simulation auf mehrere Kerne verteilen — das ist eine Minecraft-Engine-Grenze. Änderungen wirken nach einem <strong className="text-white">Neustart</strong>.
                </span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Performance-Mods */}
                <Card className="bg-ink border-line-strong text-white" noPadding>
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle className="text-lg flex items-center">
                            <Zap className="mr-2 text-ember" /> Performance-Mods
                        </CardTitle>
                        <span className="text-xs text-fg-muted capitalize">{info.loader} · MC {info.mcVersion || '?'}</span>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        {info.mods.length === 0 ? (
                            <p className="text-sm text-fg-muted">Für diesen Server (Loader: {info.loader}) gibt es keine passenden Performance-Mods.</p>
                        ) : (
                            <>
                                {presentCount > 0 && (
                                    <p className="text-xs text-green-400/90">✓ {presentCount} Performance-Mod(s) sind bereits im Pack enthalten.</p>
                                )}
                                <div className="space-y-2">
                                    {info.mods.map((m) => (
                                        <div key={m.slug} className="flex items-start gap-3 bg-surface-2 border border-line rounded-xl p-3">
                                            {m.category === 'optional' && m.status === 'available' ? (
                                                <input type="checkbox" className="mt-1 accent-[#ff6a3d]"
                                                    checked={selOptional.has(m.slug)}
                                                    onChange={(e) => setSelOptional((s) => { const n = new Set(s); e.target.checked ? n.add(m.slug) : n.delete(m.slug); return n; })} />
                                            ) : (
                                                <div className="w-[13px] shrink-0" />
                                            )}
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-bold text-sm">{m.name}</span>
                                                    {m.category === 'safe' && m.status === 'available' && <span className="text-[10px] text-ember">wird automatisch installiert</span>}
                                                    <div className="ml-auto"><StatusBadge s={m.status} /></div>
                                                </div>
                                                <p className="text-xs text-fg-muted mt-0.5">{m.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <Button className="w-full bg-ember hover:bg-ember/80" onClick={runOptimize}
                                    disabled={installing || (safeAvailable.length === 0 && selOptional.size === 0)}>
                                    {installing ? <Loader2 className="animate-spin" size={16} /> : (
                                        <><Download size={16} className="mr-2" />
                                        {safeAvailable.length + selOptional.size > 0
                                            ? `${safeAvailable.length + selOptional.size} Mod(s) installieren`
                                            : 'Alles Relevante ist installiert'}</>
                                    )}
                                </Button>
                            </>
                        )}
                    </CardContent>
                </Card>

                {/* JVM-Start-Profil */}
                <Card className="bg-ink border-line-strong text-white" noPadding>
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center">
                            <Gauge className="mr-2 text-ember" /> Start-Profil (JVM)
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            {info.jvm.profiles.map((p) => (
                                <button key={p} onClick={() => setProfile(p)}
                                    className={`w-full text-left rounded-xl border p-3 transition-all ${profile === p ? 'bg-ember/10 border-ember/50' : 'bg-surface-2 border-line hover:border-line-strong'}`}>
                                    <div className="flex items-center gap-2">
                                        <span className={`font-bold text-sm ${profile === p ? 'text-ember' : 'text-white'}`}>{PROFILE_INFO[p]?.label || p}</span>
                                        {info.jvm.profile === p && <span className="text-[10px] bg-green-500/10 text-green-400 border border-green-500/20 px-2 py-0.5 rounded-md">Aktiv</span>}
                                    </div>
                                    <p className="text-xs text-fg-muted mt-1">{PROFILE_INFO[p]?.desc}</p>
                                </button>
                            ))}
                        </div>
                        <Select label="Java-Version" value={String(javaMajor)} onChange={(v) => setJavaMajor(parseInt(v))}
                            options={info.jvm.javas.map((j) => ({
                                value: String(j.major),
                                label: `Java ${j.major}`,
                                hint: profile !== 'stabil' && j.major < 21 ? 'zu alt für ZGC' : (j.major >= 25 ? 'empfohlen für ZGC' : undefined),
                            }))} />
                        {profile !== 'stabil' && javaMajor < 21 && (
                            <p className="text-xs text-red-400">ZGC-Profile brauchen Java 21 oder neuer.</p>
                        )}
                        <Button className="w-full bg-ember hover:bg-ember/80" onClick={applyProfile}
                            disabled={applying || (profile !== 'stabil' && javaMajor < 21)}>
                            {applying ? <Loader2 className="animate-spin" size={16} /> : <><Cpu size={16} className="mr-2" /> Profil anwenden</>}
                        </Button>
                        <p className="text-[11px] text-fg-muted flex items-center gap-1.5">
                            <RotateCcw size={12} /> Wirkt nach dem nächsten Server-Neustart. Jederzeit zurück auf „Stabil" wechselbar.
                        </p>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
});
